import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { clientAPI, caseAPI, userAPI, visaProgramAPI } from '../utils/api';
import {
  ArrowLeft, User, Briefcase, CreditCard, UserCheck,
  Eye, Edit3, Phone, Mail, Globe, FileText, Clock,
  CheckCircle, AlertTriangle, Loader2, Save, X, History
} from 'lucide-react';
import { toast } from 'sonner';

const TABS = ['Overview', 'Case & Assignment', 'History'];

function InfoRow({ label, value }) {
  return (
    <div className="flex items-start justify-between py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500 w-40 flex-shrink-0">{label}</span>
      <span className="text-sm text-slate-900 font-medium text-right flex-1">{value || '—'}</span>
    </div>
  );
}

export default function AdminClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [clientCase, setClientCase] = useState(null);
  const [officers, setOfficers] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Overview');

  const [selectedOfficer, setSelectedOfficer] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [officerWorkload, setOfficerWorkload] = useState({});

  useEffect(() => { fetchAll(); }, [clientId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [clientRes, casesRes, usersRes, programsRes] = await Promise.all([
        clientAPI.getClient(clientId),
        caseAPI.getCases(),
        userAPI.getUsers(),
        visaProgramAPI.getPrograms(),
      ]);

      const clientData = clientRes.data;
      setClient(clientData);
      setPrograms(programsRes.data || []);

      const caseOfficers = (usersRes.data || []).filter(u => u.role === 'case_officer');
      setOfficers(caseOfficers);

      const allCases = casesRes.data || [];
      const found = allCases.find(c => c.client_id === clientId);
      setClientCase(found || null);
      setSelectedOfficer(found?.assigned_officer_id || '');

      // Build workload map
      const wl = {};
      caseOfficers.forEach(o => { wl[o.id] = 0; });
      allCases.forEach(c => {
        if (c.assigned_officer_id && !['Approved', 'Rejected', 'Closed'].includes(c.stage)) {
          wl[c.assigned_officer_id] = (wl[c.assigned_officer_id] || 0) + 1;
        }
      });
      setOfficerWorkload(wl);
    } catch {
      toast.error('Failed to load client details');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignOrCreate = async () => {
    setAssigning(true);
    try {
      if (!clientCase) {
        const res = await clientAPI.createCaseForClient(clientId, {
          assigned_officer_id: selectedOfficer || null
        });
        toast.success(`Case ${res.data.ucin} created${selectedOfficer ? ' and officer assigned' : ''}! Notifications sent.`);
        setClientCase(res.data.case);
      } else {
        await caseAPI.updateCase(clientCase.id, { assigned_officer_id: selectedOfficer });
        toast.success('Officer assigned! Email and in-app notification sent.');
        setClientCase({ ...clientCase, assigned_officer_id: selectedOfficer });
      }
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <div className="main-content flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-teal-600"></div>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <div className="main-content flex items-center justify-center text-slate-500">Client not found.</div>
      </div>
    );
  }

  const assignedOfficer = officers.find(o => o.id === clientCase?.assigned_officer_id);
  const program = programs.find(p => p.id === clientCase?.visa_program_id);

  const paymentStatusLabel = client.payment_status === 'full_paid' ? 'Fully Paid' : client.payment_status === 'partial_paid' ? 'Partial Payment' : client.payment_status;
  const paymentStatusColor = client.payment_status === 'full_paid' ? 'text-green-600 bg-green-50 border-green-200' : 'text-amber-600 bg-amber-50 border-amber-200';

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/clients')}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm mb-4 transition-colors"
          >
            <ArrowLeft size={16} /> Back to Clients
          </button>

          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-teal-600 flex items-center justify-center text-white text-xl font-bold">
                {client.full_name?.[0]?.toUpperCase()}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{client.full_name}</h1>
                <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                  <span className="flex items-center gap-1"><Mail size={13} />{client.email}</span>
                  {client.phone && <span className="flex items-center gap-1"><Phone size={13} />{client.phone}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${paymentStatusColor}`}>
                {paymentStatusLabel}
              </span>
              {clientCase ? (
                <button
                  onClick={() => navigate(`/admin/cases/${clientCase.id}`)}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
                >
                  <Eye size={15} /> View Case
                </button>
              ) : (
                <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold border border-amber-200">
                  No Case Yet
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 mb-6">
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-all ${
                activeTab === tab
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'Overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Personal Info */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <User size={16} className="text-teal-600" /> Personal Information
              </h3>
              <InfoRow label="Full Name" value={client.full_name} />
              <InfoRow label="Email" value={client.email} />
              <InfoRow label="Phone" value={client.phone} />
              <InfoRow label="Passport No." value={client.passport_number} />
              <InfoRow label="Nationality" value={client.nationality} />
              <InfoRow label="Country Applying" value={client.country_applying_to} />
              <InfoRow label="Visa Type" value={client.visa_type} />
              <InfoRow label="Customer Type" value={client.customer_type} />
              <InfoRow label="Sales Consultant" value={client.sales_consultant} />
            </div>

            {/* Payment Info */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <CreditCard size={16} className="text-teal-600" /> Payment Information
              </h3>
              <InfoRow label="Service Fee" value={`₹${client.payment_amount?.toLocaleString('en-IN')}`} />
              <InfoRow label="Payment Status" value={paymentStatusLabel} />
              {client.balance_due_date && (
                <InfoRow
                  label="Balance Due Date"
                  value={new Date(client.balance_due_date).toLocaleDateString()}
                />
              )}
              <InfoRow label="B2B Partner" value={client.b2b_partner_name} />
              <InfoRow
                label="Member Since"
                value={client.created_at ? new Date(client.created_at).toLocaleDateString() : '—'}
              />
            </div>

            {/* Case Summary Card */}
            <div className="md:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Briefcase size={16} className="text-teal-600" /> Case Summary
              </h3>
              {!clientCase ? (
                <div className="flex items-center gap-3 py-4 px-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle size={20} className="text-amber-500 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">No case created yet</p>
                    <p className="text-xs text-amber-600 mt-0.5">Go to the "Case & Assignment" tab to create a case and assign an officer.</p>
                  </div>
                  <button
                    onClick={() => setActiveTab('Case & Assignment')}
                    className="ml-auto px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"
                  >
                    Create Case
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 mb-1">Case ID</p>
                    <p className="text-sm font-bold text-teal-600">{clientCase.ucin}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 mb-1">Stage</p>
                    <p className="text-sm font-semibold text-slate-900">{clientCase.stage}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 mb-1">Assigned Officer</p>
                    <p className="text-sm font-semibold text-slate-900">{assignedOfficer?.full_name || <span className="text-amber-600">Unassigned</span>}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 mb-1">Visa Program</p>
                    <p className="text-sm font-semibold text-slate-900">{program ? `${program.country} – ${program.name}` : '—'}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'Case & Assignment' && (
          <div className="max-w-2xl space-y-6">
            {/* Case Status */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Briefcase size={16} className="text-teal-600" /> Case Status
              </h3>
              {!clientCase ? (
                <div className="flex items-center gap-3 py-3 px-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle size={18} className="text-amber-500" />
                  <span className="text-sm font-medium text-amber-800">No case exists for this client yet.</span>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Case ID</span>
                    <span className="text-sm font-bold text-teal-600">{clientCase.ucin}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Current Stage</span>
                    <span className="text-sm font-semibold text-slate-900">{clientCase.stage}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Created</span>
                    <span className="text-sm text-slate-700">{new Date(clientCase.created_at || clientCase.service_start_date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-slate-500">Current Officer</span>
                    <span className={`text-sm font-semibold ${assignedOfficer ? 'text-teal-700' : 'text-amber-600'}`}>
                      {assignedOfficer?.full_name || 'Not assigned yet'}
                    </span>
                  </div>
                  <div className="pt-2">
                    <button
                      onClick={() => navigate(`/admin/cases/${clientCase.id}`)}
                      className="flex items-center gap-2 text-sm text-teal-600 hover:underline"
                    >
                      <Eye size={14} /> Open Full Case Details
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Assignment Panel */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-base font-semibold text-slate-900 mb-1 flex items-center gap-2">
                <UserCheck size={16} className="text-teal-600" />
                {!clientCase ? 'Create Case & Assign Officer' : assignedOfficer ? 'Reassign Officer' : 'Assign Officer'}
              </h3>
              <p className="text-xs text-slate-500 mb-4">
                Officer receives an email + in-app notification. Client is notified in their portal.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Select Case Officer</label>
                  <select
                    value={selectedOfficer}
                    onChange={e => setSelectedOfficer(e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none text-sm"
                  >
                    <option value="">Choose an officer...</option>
                    {officers.map(o => {
                      const load = officerWorkload[o.id] || 0;
                      const badge = load >= 8 ? ' ⚠ High load' : load >= 5 ? ' · Busy' : '';
                      return (
                        <option key={o.id} value={o.id}>
                          {o.full_name} — {load} active case{load !== 1 ? 's' : ''}{badge}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Workload preview cards */}
                {officers.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {officers.map(o => {
                      const load = officerWorkload[o.id] || 0;
                      const isSelected = selectedOfficer === o.id;
                      const loadColor = load >= 8 ? 'text-red-600 bg-red-50 border-red-200' : load >= 5 ? 'text-amber-600 bg-amber-50 border-amber-200' : 'text-teal-600 bg-teal-50 border-teal-200';
                      return (
                        <button
                          key={o.id}
                          type="button"
                          onClick={() => setSelectedOfficer(o.id)}
                          className={`p-3 text-left rounded-lg border transition-all ${
                            isSelected ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-500/20' : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <p className="text-sm font-semibold text-slate-900">{o.full_name}</p>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${loadColor}`}>
                            {load} active case{load !== 1 ? 's' : ''}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                <button
                  onClick={handleAssignOrCreate}
                  disabled={assigning || !selectedOfficer}
                  className="w-full py-2.5 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                >
                  {assigning ? (
                    <><Loader2 size={18} className="animate-spin" /> Processing...</>
                  ) : (
                    <><UserCheck size={18} /> {!clientCase ? 'Create Case & Assign Officer' : 'Assign Officer & Notify'}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'History' && (
          <div className="max-w-2xl">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <History size={16} className="text-teal-600" /> Assignment History
              </h3>
              {assignmentHistory.length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center">No assignment history yet.</p>
              ) : (
                <div className="space-y-3">
                  {assignmentHistory.map((h, i) => (
                    <div key={i} className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <UserCheck size={14} className="text-teal-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          Assigned to <strong>{h.to_officer_name}</strong>
                        </p>
                        <p className="text-xs text-slate-500">
                          By {h.assigned_by_name} · {new Date(h.timestamp).toLocaleString()}
                        </p>
                        {h.from_officer_id && (
                          <p className="text-xs text-slate-400 mt-0.5">Reassigned from previous officer</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
